/**
 * Purchase Order Form Modal
 *
 * Create and edit purchase orders in a modal with deep linking support.
 * View mode uses a Drawer (slide-over), edit/create mode uses a centered Modal.
 *
 * @see US-8.15 - Formulaire Creation Commande
 */

import { Send, Package, Copy, XCircle } from 'lucide-react';
import { Modal, ModalFooter, Skeleton, Button, Drawer } from '@/components/ui';
import { usePurchaseOrderForm } from '../hooks/usePurchaseOrderForm';
import {
  OrderFormHeader,
  OrderInfoSection,
  OrderProductsSection,
  OrderReadOnlyView,
} from './order-form';
import { PurchaseOrderStatus } from '@bstock/shared';
import type { ModalMode } from '@/hooks';

interface PurchaseOrderFormModalProps {
  isOpen: boolean;
  mode: ModalMode;
  orderId: string | null;
  onClose: () => void;
  onOpenEdit: (id: string) => void;
  showFinancials?: boolean;
}

export function PurchaseOrderFormModal({
  isOpen,
  mode,
  orderId,
  onClose,
  onOpenEdit,
  showFinancials = false,
}: PurchaseOrderFormModalProps) {
  const form = usePurchaseOrderForm({
    isOpen,
    mode,
    orderId,
    onClose,
    onOpenEdit,
  });

  const isViewMode = mode === 'view';
  const isReadOnly = form.isEditMode && form.existingOrder && form.existingOrder.status !== PurchaseOrderStatus.DRAFT;

  // ==========================================
  // VIEW MODE → Drawer (slide-over)
  // ==========================================

  if (isViewMode || isReadOnly) {
    // Loading
    if (form.isEditMode && form.orderLoading) {
      return (
        <Drawer
          open={isOpen}
          onOpenChange={(open) => { if (!open) onClose(); }}
          title="Chargement..."
          size="full"
        >
          <div className="space-y-6">
            <Skeleton className="h-20" />
            <Skeleton className="h-64" />
          </div>
        </Drawer>
      );
    }

    if (!form.existingOrder) return null;

    const order = form.existingOrder;
    const isSent = order.status === PurchaseOrderStatus.SENT;
    const isPartiallyReceived = order.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
    const canReceive = isSent || isPartiallyReceived;

    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => { if (!open) onClose(); }}
        title={`Commande ${order.orderNumber}`}
        description={order.supplier.name}
        size="full"
        footer={
          <div className="flex items-center gap-3">
            {canReceive && (
              <Button variant="primary" onClick={form.handleReceive}>
                <Package className="w-4 h-4" />
                Recevoir
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={form.handleDuplicateOrder}
              disabled={form.isDuplicating}
            >
              <Copy className="w-4 h-4" />
              {form.isDuplicating ? 'Duplication...' : 'Dupliquer'}
            </Button>
            {isSent && (
              <Button
                variant="danger"
                onClick={form.openCancelConfirm}
                disabled={form.isCancelling}
              >
                <XCircle className="w-4 h-4" />
                {form.isCancelling ? 'Annulation...' : 'Annuler'}
              </Button>
            )}
          </div>
        }
      >
        <OrderReadOnlyView
          order={order}
          onCancelOrder={form.handleCancelOrder}
          showCancelConfirm={form.showCancelConfirm}
          onCloseCancelConfirm={form.closeCancelConfirm}
          isCancelling={form.isCancelling}
          showFinancials={showFinancials}
        />
      </Drawer>
    );
  }

  // ==========================================
  // CREATE / EDIT MODE → Modal (centered)
  // ==========================================

  // Loading state
  if (form.isEditMode && form.orderLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Chargement..." size="4xl">
        <div className="space-y-6">
          <Skeleton className="h-20" />
          <Skeleton className="h-64" />
        </div>
      </Modal>
    );
  }

  // Draft mode - editable form
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" className="h-[85vh]">
      {/* Header */}
      <OrderFormHeader
        orderNumber={form.existingOrder?.orderNumber ?? null}
        supplierName={form.isEditMode && form.existingOrder ? form.existingOrder.supplier.name : null}
        status={PurchaseOrderStatus.DRAFT}
        isSaving={form.isSaving}
        isSending={form.isSending}
        isFormValid={form.isFormValid}
        hasSupplierId={Boolean(form.supplierId)}
        onSave={form.handleSave}
        onSendClick={form.handleSendClick}
      />

      {/* Form content */}
      <div className="flex flex-col flex-1 min-h-0 gap-6">
        {/* Order info section */}
        <div className="shrink-0">
          <OrderInfoSection
            supplierId={form.supplierId}
            expectedDateRange={form.expectedDateRange}
            supplierOptions={form.supplierOptions}
            suppliersLoading={form.suppliersLoading}
            isEditMode={form.isEditMode}
            onSupplierChange={form.handleSupplierChange}
            onExpectedDateChange={form.handleExpectedDateChange}
          />
        </div>

        {/* Products section - takes remaining space */}
        <OrderProductsSection
          items={form.items}
          supplierId={form.supplierId || null}
          readOnly={false}
          onChange={form.handleItemsChange}
        />
      </div>

      {/* Supplier change confirmation modal */}
      <Modal
        isOpen={form.showSupplierChangeConfirm}
        onClose={form.cancelSupplierChange}
        title="Changer de fournisseur ?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-4">
          Si vous changez de fournisseur, les {form.items.length} ligne(s) de produits seront
          supprimees car les produits sont specifiques a chaque catalogue fournisseur.
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={form.cancelSupplierChange}>
            Annuler
          </Button>
          <Button variant="danger" onClick={form.confirmSupplierChange}>
            Changer de fournisseur
          </Button>
        </ModalFooter>
      </Modal>

      {/* Send confirmation modal */}
      <Modal
        isOpen={form.showSendConfirm}
        onClose={form.cancelSendConfirm}
        title="Envoyer la commande ?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-4">
          Une fois envoyee, la commande ne sera plus modifiable. Voulez-vous continuer ?
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={form.cancelSendConfirm}>
            Annuler
          </Button>
          <Button variant="primary" onClick={form.handleSend}>
            <Send className="w-4 h-4" />
            Envoyer la commande
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
}
